import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  IconButton, 
  Avatar, 
  Typography, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText,
  Divider,
  Badge,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { sendMessage } from '../../services/chatServices';
import './ChatInterface.css';

function ChatInterface() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { threadId } = useParams();
  const location = useLocation();
  const initialMessage = location.state?.initialMessage;
  const firstResponse = location.state?.firstResponse;
  const skipInitialSend = location.state?.skipInitialSend;
  const hasProcessedInitialMessage = useRef(false);
  
  useEffect(() => {
    document.body.classList.add('chat-interface-page');
    return () => {
      document.body.classList.remove('chat-interface-page');
    };
  }, []);

  const [messages, setMessages] = useState(() => {
    // If we have an initial message from home, show it and its response
    if (initialMessage) {
      const initialMessages = [{
        id: 1,
        text: initialMessage,
        sender: 'user',
        timestamp: new Date(),
        type: 'user'
      }];

      if (firstResponse) {
        initialMessages.push({
          id: 2,
          text: firstResponse,
          sender: 'ai',
          timestamp: new Date(),
          type: 'ai'
        });
      }

      return initialMessages;
    }
    
    // Otherwise show the default welcome message
    return [{
      id: 1,
      text: "I'm an advanced AI assistant ready to help you with any questions or tasks. How can I assist you today?",
      sender: 'system',
      timestamp: new Date(),
      type: 'system'
    }];
  });

  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleSendMessage = useCallback(async (e, messageText = null) => {
    if (e) {
      e.preventDefault();
    }
    
    const textToSend = messageText || newMessage;
    if (!textToSend.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: textToSend,
      sender: 'user',
      timestamp: new Date(),
      type: 'user'
    };

    setMessages(prev => [...prev, userMessage]);
    if (!messageText) {
      setNewMessage('');
    }
    
    setIsTyping(true);
    try {
      const response = await sendMessage(threadId, textToSend);
      const aiResponse = {
        id: messages.length + 2,
        text: response,
        sender: 'ai',
        timestamp: new Date(),
        type: 'ai'
      };
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorResponse = {
        id: messages.length + 2,
        text: "Sorry, I encountered an error while processing your request. Please try again.",
        sender: 'ai',
        timestamp: new Date(),
        type: 'ai'
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  }, [messages, newMessage, threadId]);

  // Handle initial message and response
  useEffect(() => {
    if (hasProcessedInitialMessage.current || !initialMessage || skipInitialSend) return;
    
    hasProcessedInitialMessage.current = true;
    handleSendMessage(null, initialMessage);
  }, [initialMessage, skipInitialSend, handleSendMessage]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  const renderMessageContent = (message) => {
    if (message.type === 'system') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">{message.text}</Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            '& .MuiChip-root': {
              m: isMobile ? 0.25 : 0.5
            }
          }}>
            <Chip 
              label="Code Generation" 
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Chip 
              label="AI Assistant" 
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <Chip 
              label="Natural Language" 
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            />
          </Box>
        </Box>
      );
    }
    return <ReactMarkdown>{message.text}</ReactMarkdown>;
  };

  return (
    <Box className="full-screen">
      <Box className="chat-container">
        {/* Messages List */}
        <Box className="messages-container">
          <List sx={{ pt: 0 }}>
            {messages.map((message) => (
              <React.Fragment key={message.id}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{
                    py: isMobile ? 1 : 2
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: 'white'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: 'white'
                        }}
                      >
                        {message.type === 'user' ? 'U' : 'AI'}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={renderMessageContent(message)}
                    secondary={
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'var(--text-secondary)',
                          fontSize: isMobile ? '0.7rem' : '0.75rem'
                        }}
                      >
                        {format(message.timestamp, 'h:mm a')}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider 
                  variant="inset" 
                  component="li" 
                  sx={{ 
                    borderColor: 'var(--border-color)',
                    my: isMobile ? 0.5 : 1
                  }} 
                />
              </React.Fragment>
            ))}
            {isTyping && (
              <ListItem
                sx={{
                  py: isMobile ? 1 : 2
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'white'
                    }}
                  >
                    AI
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'var(--text-secondary)',
                          fontSize: isMobile ? '0.8rem' : '0.875rem'
                        }}
                      >
                        AI is processing
                      </Typography>
                      <Box className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>

        {/* Message Input */}
        <Box 
          component="form" 
          onSubmit={handleSendMessage} 
          className="input-container"
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask me anything..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              size="small"
              multiline
              maxRows={isMobile ? 3 : 4}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'var(--text-secondary)'
                  }
                }
              }}
            />
            <IconButton 
              type="submit" 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
                minWidth: '40px',
                height: '40px'
              }}
              disabled={isTyping}
              data-has-text={newMessage.trim().length > 0}
            >
              <SendIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ChatInterface;